import { Link } from "react-router-dom";

const SECTIONS = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    content: [
      {
        heading: "Agreement to these terms",
        body: "By accessing or using imperium.chat, registering for a webinar, requesting a demo, or subscribing to any Imperium service, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, do not use our services.",
      },
      {
        heading: "Eligibility",
        body: "You must be at least 18 years of age and have the legal authority to enter into binding contracts on behalf of yourself or the business you represent. By using our services, you represent and warrant that you meet these requirements.",
      },
      {
        heading: "Updates to these terms",
        body: "Imperium reserves the right to modify these Terms of Service at any time. We will notify you of material changes by updating the effective date on this page. Your continued use of our services after any modification constitutes acceptance of the revised terms.",
      },
    ],
  },
  {
    id: "services",
    title: "2. Description of Services",
    content: [
      {
        heading: "What Imperium provides",
        body: "Imperium provides an AI-powered outbound calling platform designed for title companies and mortgage professionals. Our platform enables automated outreach triggered by real-time listing, pending, and closing activity, using artificial intelligence to conduct voice calls to real estate agents and loan officers on behalf of subscribing businesses.",
      },
      {
        heading: "Webinars and educational content",
        body: "We may offer free live webinars, recorded content, and educational materials. These are provided for informational purposes only and do not constitute professional legal, financial, or compliance advice. Attendance at or registration for a webinar does not create a client relationship.",
      },
      {
        heading: "Service availability",
        body: "We strive to maintain high availability of our platform but do not guarantee uninterrupted service. Scheduled maintenance, updates, or circumstances beyond our control may result in temporary downtime. Imperium is not liable for losses resulting from service interruptions.",
      },
    ],
  },
  {
    id: "accounts",
    title: "3. Accounts and Access",
    content: [
      {
        heading: "Account registration",
        body: "To access certain features of our platform, you must create an account. You agree to provide accurate, current, and complete information during registration and to keep that information up to date. Each account is for use by a single subscribing business entity.",
      },
      {
        heading: "Account security",
        body: "You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account. You agree to notify Imperium immediately at support@imperium.chat if you suspect any unauthorized access to or use of your account.",
      },
      {
        heading: "Account suspension",
        body: "Imperium reserves the right to suspend or terminate your account at any time if we have reason to believe you have violated these Terms of Service, engaged in fraudulent activity, or used the platform in a manner that could harm Imperium, other customers, or third parties.",
      },
    ],
  },
  {
    id: "acceptable-use",
    title: "4. Acceptable Use",
    content: [
      {
        heading: "Permitted use",
        body: "You may use the Imperium platform solely for lawful business outreach purposes in connection with your title company or mortgage professional practice. You agree to use the platform only in compliance with all applicable federal, state, and local laws and regulations.",
      },
      {
        heading: "Prohibited activities",
        body: "You may not use our platform to: (a) violate any applicable law or regulation; (b) send unsolicited communications to individuals who have not given appropriate consent; (c) impersonate any person or entity; (d) transmit harmful, harassing, defamatory, or fraudulent content; (e) attempt to gain unauthorized access to any system or network; (f) resell, sublicense, or redistribute access to the platform without written permission from Imperium.",
      },
      {
        heading: "TCPA and telemarketing compliance",
        body: "You are solely responsible for ensuring that all calls initiated through the Imperium platform comply with the Telephone Consumer Protection Act (TCPA), the FTC Telemarketing Sales Rule, and all applicable state telemarketing laws. This includes maintaining proper consent records, honoring do-not-call requests, and complying with all calling time restrictions. Imperium provides the technology platform only — compliance with applicable law is your responsibility as the subscribing business.",
      },
    ],
  },
  {
    id: "payment",
    title: "5. Fees and Payment",
    content: [
      {
        heading: "Subscription fees",
        body: "Access to the Imperium platform requires a paid subscription. Current pricing is available at imperium.chat/#pricing. We reserve the right to change our pricing with reasonable advance notice to existing subscribers.",
      },
      {
        heading: "Billing",
        body: "Subscription fees are billed in advance on a monthly or annual basis depending on the plan you select. All fees are non-refundable except as expressly stated in these terms or required by law.",
      },
      {
        heading: "Failed payments",
        body: "If a payment fails, we will attempt to notify you and may suspend access to your account until payment is resolved. Continued failure to pay may result in account termination.",
      },
      {
        heading: "Taxes",
        body: "You are responsible for all taxes, duties, and other governmental fees applicable to your use of our services, excluding taxes based on Imperium's net income.",
      },
    ],
  },
  {
    id: "intellectual-property",
    title: "6. Intellectual Property",
    content: [
      {
        heading: "Imperium's intellectual property",
        body: "All content, software, technology, trademarks, and materials available through the Imperium platform — including but not limited to the AI voice system, dashboard, call scripts, and training materials — are the exclusive property of Imperium AI, Inc. and are protected by applicable intellectual property laws. Nothing in these terms grants you any ownership rights in our platform or content.",
      },
      {
        heading: "Your content",
        body: "You retain ownership of any data, contact lists, or content you upload to the platform. By using our services, you grant Imperium a limited license to process and use that content solely to provide the services you have subscribed to. We do not claim ownership of your data.",
      },
      {
        heading: "Feedback",
        body: "If you provide feedback, suggestions, or ideas about our platform, you grant Imperium an irrevocable, royalty-free license to use that feedback in any manner without restriction or compensation to you.",
      },
    ],
  },
  {
    id: "confidentiality",
    title: "7. Confidentiality",
    content: [
      {
        heading: "Mutual confidentiality",
        body: "Each party agrees to keep the other's non-public business information confidential and not to disclose it to any third party without prior written consent, except as required by law. This obligation survives termination of these terms for a period of three years.",
      },
      {
        heading: "Exclusions",
        body: "Confidentiality obligations do not apply to information that: (a) is or becomes publicly available through no fault of the receiving party; (b) was rightfully known to the receiving party prior to disclosure; (c) is independently developed by the receiving party without use of the confidential information; or (d) is required to be disclosed by law or court order.",
      },
    ],
  },
  {
    id: "disclaimers",
    title: "8. Disclaimers and Limitation of Liability",
    content: [
      {
        heading: "No guarantee of results",
        body: "Imperium does not guarantee any specific outcome, including a specific number of conversations, closings, or revenue generated through use of our platform. Results vary based on market conditions, agent responsiveness, territory, and how the platform is used.",
      },
      {
        heading: "Disclaimer of warranties",
        body: "THE IMPERIUM PLATFORM AND ALL RELATED SERVICES ARE PROVIDED \"AS IS\" AND \"AS AVAILABLE\" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.",
      },
      {
        heading: "Limitation of liability",
        body: "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IMPERIUM'S TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR YOUR USE OF OUR SERVICES SHALL NOT EXCEED THE AMOUNT YOU PAID TO IMPERIUM IN THE THREE MONTHS PRECEDING THE CLAIM. IN NO EVENT SHALL IMPERIUM BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES.",
      },
    ],
  },
  {
    id: "indemnification",
    title: "9. Indemnification",
    content: [
      {
        heading: "Your obligation to indemnify",
        body: "You agree to indemnify, defend, and hold harmless Imperium AI, Inc. and its officers, directors, employees, and agents from and against any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising out of or related to: (a) your use of the platform; (b) your violation of these Terms of Service; (c) your violation of any applicable law, including the TCPA; or (d) any claim by a third party arising from calls made through your account.",
      },
    ],
  },
  {
    id: "termination",
    title: "10. Termination",
    content: [
      {
        heading: "Termination by you",
        body: "You may cancel your subscription at any time by contacting support@imperium.chat. Cancellation takes effect at the end of your current billing period. No refunds are issued for partial billing periods.",
      },
      {
        heading: "Termination by Imperium",
        body: "Imperium may terminate your access to the platform immediately and without notice if you violate these Terms of Service, engage in illegal activity using our platform, or if required to do so by law.",
      },
      {
        heading: "Effect of termination",
        body: "Upon termination, your right to access and use the platform ceases immediately. Sections of these terms that by their nature should survive termination — including intellectual property, disclaimers, limitation of liability, and indemnification — will continue to apply.",
      },
    ],
  },
  {
    id: "governing-law",
    title: "11. Governing Law and Disputes",
    content: [
      {
        heading: "Governing law",
        body: "These Terms of Service are governed by the laws of the State of Delaware, without regard to its conflict of law provisions.",
      },
      {
        heading: "Dispute resolution",
        body: "Any dispute arising out of or relating to these terms or your use of the Imperium platform shall first be addressed through good-faith negotiation between the parties. If the dispute cannot be resolved informally, the parties agree to submit the matter to binding arbitration in accordance with the rules of the American Arbitration Association.",
      },
      {
        heading: "Class action waiver",
        body: "You agree that any disputes will be resolved on an individual basis only, and not as part of any class, consolidated, or representative action.",
      },
    ],
  },
  {
    id: "general",
    title: "12. General Provisions",
    content: [
      {
        heading: "Entire agreement",
        body: "These Terms of Service and our Privacy Policy constitute the entire agreement between you and Imperium with respect to your use of our services and supersede all prior agreements or understandings.",
      },
      {
        heading: "Severability",
        body: "If any provision of these terms is found to be unenforceable, that provision will be modified to the minimum extent necessary to make it enforceable, and the remaining provisions will continue in full force and effect.",
      },
      {
        heading: "Waiver",
        body: "Imperium's failure to enforce any right or provision of these terms shall not constitute a waiver of that right or provision.",
      },
      {
        heading: "Contact",
        body: "If you have questions about these Terms of Service, contact us at legal@imperium.chat.",
      },
    ],
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      <title>Terms of Service | Imperium</title>
      <meta
        name="description"
        content="Imperium Terms of Service — the rules and conditions governing your use of the Imperium AI-powered outbound calling platform."
      />
      <link rel="canonical" href="https://imperium.chat/terms" />
      <meta name="robots" content="noindex, follow" />

      {/* Navbar */}
      <nav className="border-b border-gray-100 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-navy font-extrabold text-xl tracking-tight whitespace-nowrap">
            IMPERIUM
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm font-medium text-[#4a5568] hover:text-navy transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm" />
            </span>
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-[#f8fafc] border-b border-gray-100 py-10 md:py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-5">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-file-text-line text-primary text-xs" />
            </span>
            <span className="text-xs font-semibold text-primary">Legal</span>
          </div>
          <h1 className="text-navy font-extrabold text-3xl sm:text-4xl mb-4 leading-tight">Terms of Service</h1>
          <p className="text-[#4a5568] text-base leading-relaxed mb-3">
            These Terms of Service govern your access to and use of the Imperium platform, website at{" "}
            <strong>imperium.chat</strong>, and all related services provided by Imperium AI, Inc.
            Please read them carefully before using our services.
          </p>
          <p className="text-[#4a5568] text-sm">
            <strong>Effective date:</strong> March 23, 2026 &nbsp;·&nbsp;
            <strong>Last updated:</strong> March 23, 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">

        {/* Quick nav */}
        <div className="bg-[#f8fafc] border border-gray-200 rounded-2xl p-5 sm:p-6 mb-8 md:mb-14">
          <p className="text-xs font-semibold text-navy uppercase tracking-widest mb-4">Contents</p>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="text-sm text-[#4a5568] hover:text-primary transition-colors cursor-pointer"
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </div>

        {/* Sections */}
        <div className="flex flex-col gap-10 md:gap-14">
          {SECTIONS.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-24">
              <h2 className="text-navy font-bold text-xl mb-6 pb-3 border-b border-gray-100">
                {section.title}
              </h2>
              <div className="flex flex-col gap-6">
                {section.content.map((block) => (
                  <div key={block.heading}>
                    <h3 className="text-navy font-semibold text-sm mb-2">{block.heading}</h3>
                    <p className="text-[#4a5568] text-sm leading-relaxed">{block.body}</p>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Also see Privacy Policy */}
        <div className="mt-10 md:mt-14 rounded-2xl bg-[#f8fafc] border border-gray-200 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <span className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-xl flex-shrink-0">
            <i className="ri-shield-check-line text-primary text-lg" />
          </span>
          <div className="flex-1">
            <p className="text-navy font-semibold text-sm mb-1">Also review our Privacy Policy</p>
            <p className="text-[#4a5568] text-sm">
              Our Privacy Policy explains how we collect, use, and protect your personal information.
            </p>
          </div>
          <Link
            to="/privacy"
            className="inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-navy/90 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            Read Privacy Policy
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm" />
            </span>
          </Link>
        </div>

        {/* Bottom CTA */}
        <div className="mt-8 md:mt-10 pt-8 md:pt-10 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <p className="text-navy font-semibold text-sm mb-1">Questions about these terms?</p>
            <p className="text-[#4a5568] text-sm">
              Email us at{" "}
              <a
                href="mailto:legal@imperium.chat"
                className="text-primary hover:underline cursor-pointer"
              >
                legal@imperium.chat
              </a>
            </p>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-navy/90 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm" />
            </span>
            Back to Imperium
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 px-4 sm:px-6 lg:px-8 bg-[#f8fafc]">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-[#4a5568]">
            &copy; {new Date().getFullYear()} Imperium AI, Inc. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-xs text-[#4a5568] hover:text-navy transition-colors whitespace-nowrap">Privacy Policy</Link>
            <Link to="/terms" className="text-xs text-primary font-medium whitespace-nowrap">Terms of Service</Link>
            <Link to="/" className="text-xs text-[#4a5568] hover:text-navy transition-colors whitespace-nowrap">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
